import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { API_BASE } from '../utils/supabase/info';
import { getVersionChanges, TERMS_CHANGELOG } from '../utils/terms-version';
import { devError } from '../utils/logger';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { FullscreenShell } from './layout/FullscreenShell';


interface TermsUpdateModalProps {
  accessToken: string;
  currentVersion: string;
  newVersion: string;
  onAccept: () => void;
  /** When set (e.g. 10001), modal is shown above admin overlay (z-[10000]) */
  portalZIndex?: number;
}

export function TermsUpdateModal({ accessToken, currentVersion, newVersion, onAccept, portalZIndex }: TermsUpdateModalProps) {
  useModalFocus(true, { fullscreen: true }); // Full-screen: hide header/footer while open
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showFullTerms, setShowFullTerms] = useState(false);

  // Scroll to top when modal appears
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleAccept = async () => {
    if (!accepted) {
      setError('Du må godta de oppdaterte vilkårene for å fortsette');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${API_BASE}/accept-terms`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            version: newVersion,
            acceptedAt: new Date().toISOString(),
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Kunne ikke lagre godkjenning');
        setLoading(false);
        return;
      }

      onAccept();
    } catch (err) {
      devError('Error accepting terms:', err);
      setError('Kunne ikke lagre godkjenning. Prøv igjen.');
      setLoading(false);
    }
  };

  // Get changes dynamically from changelog
  const changes = getVersionChanges(newVersion);
  const versionInfo = TERMS_CHANGELOG[newVersion as keyof typeof TERMS_CHANGELOG];

  const z = portalZIndex ?? 'var(--z-important)';
  return (
    <FullscreenShell
      title="Oppdaterte vilkår"
      onClose={() => {}}
      onBack={() => {}}
      canClose={false}
      showDesktopBackdrop={false}
      zIndex={z}
      containerClassName="xl:max-w-3xl"
      contentClassName="px-4 sm:px-6 py-6 sm:py-8 pt-page-safe md:mt-10 lg:mt-14 xl:mt-20 pb-24 pb-safe"
    >
      <div className="flex items-start gap-4 mb-6">
        <div className="p-3 bg-purple-100 rounded-full flex-shrink-0">
          <AlertCircle className="w-8 h-8 text-purple-600" />
        </div>
        <div>
          <h2 className="text-2xl sm:text-3xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
            Oppdaterte vilkår og betingelser
          </h2>
          <p className="text-gray-600 mt-2">
            Vi har oppdatert våre vilkår og betingelser. Du må godta de nye vilkårene for å fortsette å bruke Talentfunn.
          </p>
        </div>
      </div>

          {/* Version info */}
          <div className="bg-purple-50 p-4 rounded-xl border-2 border-purple-100 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Din versjon:</p>
                <p className="text-lg text-purple-900">{currentVersion}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Ny versjon:</p>
                <p className="text-lg text-purple-900">{newVersion}</p>
              </div>
            </div>
          </div>

          {/* Summary of changes */}
          <div className="mb-6">
            <h3 className="text-lg text-purple-900 mb-3">Viktige endringer:</h3>
            {versionInfo && (
              <p className="text-sm text-gray-600 mb-3">
                Oppdatert: {versionInfo.date}
              </p>
            )}
            <div className="space-y-2">
              {changes.length > 0 ? (
                changes.map((change, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200"
                  >
                    <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-800">{change}</p>
                  </div>
                ))
              ) : (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-600">Generelle oppdateringer og forbedringer</p>
                </div>
              )}
            </div>
          </div>

          {/* Show full terms button */}
          <button
            onClick={() => setShowFullTerms(!showFullTerms)}
            className="text-purple-600 hover:text-purple-700 underline mb-4"
          >
            {showFullTerms ? 'Skjul fullstendige vilkår' : 'Vis fullstendige vilkår'}
          </button>

          {/* Full terms content */}
          {showFullTerms && (
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-6 max-h-96 overflow-y-auto">
              <div className="prose prose-sm max-w-none">
                <h3 className="text-purple-900">Fullstendige vilkår og betingelser</h3>
                <p className="text-gray-700">
                  <strong>Sist oppdatert:</strong> {versionInfo?.date || '28. januar 2025'}
                </p>
                <p className="text-gray-700 text-sm mt-4">
                  For å se de fullstendige vilkårene, gå til «Vilkår og betingelser» i bunnteksten etter at du har godtatt denne oppdateringen.
                </p>
              </div>
            </div>
          )}

          {/* Checkbox */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border-2 border-purple-200 mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => {
                  setAccepted(e.target.checked);
                  setError('');
                }}
                className="mt-1 w-5 h-5 text-purple-600 border-2 border-purple-300 rounded focus:ring-2 focus:ring-purple-500"
              />
              <span className="text-gray-800">
                Jeg har lest og godtar de oppdaterte vilkårene og betingelsene for Talentfunn. Jeg forstår at jeg må godta disse for å fortsette å bruke tjenesten.
              </span>
            </label>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleAccept}
              disabled={loading || !accepted}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Lagrer...' : 'Godta og fortsett'}
            </button>
          </div>

          <p className="text-sm text-gray-500 text-center mt-4">
            Du må godta de nye vilkårene for å kunne bruke Talentfunn. Hvis du ikke godtar, vil du bli logget ut.
          </p>
    </FullscreenShell>
  );
}
